import { isHeicFile, prepareImageForUpload } from "@/lib/imageFileHelpers";
import {
  getUploadConfig,
  mergePlatformConstants,
} from "@/lib/platformConstants/defaults";
import { showLoadingToast, toast } from "@/components/ui/sonner";
import { uploadFile } from "@/lib/upload/uploadFile";
import { UploadContextKey, type UploadedAsset } from "@/lib/upload/types";

/** File picker `accept` — JPG, PNG, SVG, PDF. */
export const INSPIRATION_FILE_ACCEPT =
  ".jpg,.jpeg,.png,.svg,.pdf,image/jpeg,image/png,image/svg+xml,application/pdf";

const ALLOWED_INSPIRATION_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/svg+xml",
  "application/pdf",
]);

const ALLOWED_INSPIRATION_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "svg",
  "pdf",
]);

export const DEFAULT_INSPIRATION_MAX_BYTES = getUploadConfig(
  mergePlatformConstants(null),
  "design-request-inspiration",
).maxBytesPerFile;

export type InspirationPreviewKind = "image" | "svg" | "pdf";

export function getInspirationFileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

export function isAllowedInspirationFile(file: File): boolean {
  if (ALLOWED_INSPIRATION_TYPES.has(file.type)) return true;
  return ALLOWED_INSPIRATION_EXTENSIONS.has(getInspirationFileExtension(file.name));
}

export function validateInspirationFile(
  file: File,
  maxBytes = DEFAULT_INSPIRATION_MAX_BYTES,
): string | null {
  if (!isAllowedInspirationFile(file)) {
    return `${file.name} is not supported. Use JPG, PNG, SVG, or PDF.`;
  }
  if (file.size > maxBytes) {
    const limitMb = Math.round(maxBytes / (1024 * 1024));
    return `${file.name} is over the ${limitMb} MB limit.`;
  }
  return null;
}

export function getInspirationPreviewKind(
  name: string,
  contentType?: string,
  url?: string | null,
): InspirationPreviewKind {
  const ext = getInspirationFileExtension(name);
  if (ext === "pdf" || contentType === "application/pdf") return "pdf";
  if (ext === "svg" || contentType === "image/svg+xml") return "svg";

  const path = (url ?? "").split("?")[0]?.toLowerCase() ?? "";
  if (path.endsWith(".pdf")) return "pdf";
  if (path.endsWith(".svg")) return "svg";
  if (/\.(jpe?g|png|webp|gif|heic|heif)$/.test(path)) return "image";

  // Name/URL give no cue — treat as image when a src exists; callers fall back
  // to the document badge if the <img> fails to load.
  return "image";
}

export function canShowInspirationImagePreview(
  kind: InspirationPreviewKind,
  src?: string | null,
): boolean {
  return !!src && (kind === "image" || kind === "svg");
}

const RASTER_NAME_RE = /\.(jpe?g|png|webp)$/i;

function isRasterForCompression(file: File): boolean {
  return (
    RASTER_NAME_RE.test(file.name) ||
    /^image\/(jpe?g|png|webp)$/i.test(file.type)
  );
}

/** Raster / HEIC → optimized JPEG/PNG; SVG and PDF pass through unchanged. */
export async function prepareInspirationFileForUpload(file: File): Promise<File> {
  if (isHeicFile(file) || isRasterForCompression(file)) {
    return prepareImageForUpload(file);
  }
  return file;
}

export function truncateInspirationFileName(name: string, max = 18): string {
  if (name.length <= max) return name;
  return `${name.slice(0, max - 1)}…`;
}

export function inspirationTypeBadgeLabel(
  kind: InspirationPreviewKind,
  name: string,
): string {
  if (kind === "pdf") return "PDF";
  if (kind === "svg") return "SVG";
  const ext = getInspirationFileExtension(name).toUpperCase();
  return ext || "FILE";
}

/** Compress if needed, upload via API, with one loading toast for the whole operation. */
export async function uploadInspirationFile(
  file: File,
  maxBytesPerFile: number,
): Promise<UploadedAsset> {
  const toastId = showLoadingToast("Uploading file…");
  try {
    const prepared = await prepareInspirationFileForUpload(file);
    const asset = await uploadFile(prepared, {
      key: UploadContextKey.DesignRequestInspiration,
      maxBytesPerFile,
      showLoadingToast: false,
    });
    toast.dismiss(toastId);
    return asset;
  } catch (error) {
    toast.dismiss(toastId);
    throw error;
  }
}
